import { IntervalType } from '../types/data-set.types';

export interface PatientMeasurement {
  date?: string; // Date of measurement in ISO format
  age?: number; // Age at measurement in the chart's units (weeks/months)
  length?: number; // Length in cm (for weight-for-length charts)
  value: number; // Measurement value in the chart's units (cm, kg, etc)
  percentile?: number; // Optional calculated percentile
}

export interface PatientInfo {
  gestationalAgeAtBirth?: number; // Gestational age in weeks at birth (40 is full term)
  measurements: PatientMeasurement[];
}

// Number of weeks considered full term
const FULL_TERM_WEEKS = 40;

export const mockPatientData: Record<string, PatientInfo> = {
  // Length/height measurements - weekly (0-13 weeks)
  'who-boys-length-weekly': {
    gestationalAgeAtBirth: 34, // Born at 34 weeks (6 weeks premature)
    measurements: [
      { date: '2023-01-01', age: 0, value: 48.1 },
      { date: '2023-01-08', age: 1, value: 48.9 },
      { date: '2023-01-15', age: 2, value: 50.1 },
      { date: '2023-01-22', age: 3, value: 51.2 },
      { date: '2023-01-29', age: 4, value: 52.3 },
      { date: '2023-02-12', age: 6, value: 54.5 },
      { date: '2023-02-26', age: 8, value: 56.7 },
      { date: '2023-03-19', age: 11, value: 59.8 },
      { date: '2023-04-02', age: 13, value: 61.2 },
    ],
  },
  'who-girls-length-weekly': {
    gestationalAgeAtBirth: 36, // Born at 36 weeks (4 weeks premature)
    measurements: [
      { date: '2023-01-01', age: 0, value: 47.3 },
      { date: '2023-01-08', age: 1, value: 48.1 },
      { date: '2023-01-15', age: 2, value: 49.1 },
      { date: '2023-01-22', age: 3, value: 50.0 },
      { date: '2023-01-29', age: 4, value: 51.2 },
      { date: '2023-02-12', age: 6, value: 53.3 },
      { date: '2023-02-26', age: 8, value: 55.2 },
      { date: '2023-03-19', age: 11, value: 57.3 },
      { date: '2023-04-02', age: 13, value: 58.9 },
    ],
  },

  // Length/height measurements - monthly
  'who-boys-height-monthly': {
    gestationalAgeAtBirth: 34, // Born at 34 weeks (6 weeks premature)
    measurements: [
      { date: '2023-01-01', age: 0, value: 48.1 },
      { date: '2023-02-01', age: 1, value: 51.8 },
      { date: '2023-03-01', age: 2, value: 55.4 },
      { date: '2023-05-01', age: 4, value: 60.2 },
      { date: '2023-07-01', age: 6, value: 64.5 },
      { date: '2023-10-01', age: 9, value: 69.8 },
      { date: '2024-01-01', age: 12, value: 74.3 },
      { date: '2024-07-01', age: 18, value: 80.2 },
      { date: '2025-01-01', age: 24, value: 85.1 },
      { date: '2026-01-01', age: 36, value: 94.9 },
      { date: '2027-01-01', age: 48, value: 101.4 },
      { date: '2028-01-01', age: 60, value: 108.3 },
    ],
  },
  'who-girls-height-monthly': {
    gestationalAgeAtBirth: 36, // Born at 36 weeks (4 weeks premature)
    measurements: [
      { date: '2023-01-01', age: 0, value: 47.3 },
      { date: '2023-02-01', age: 1, value: 51.2 },
      { date: '2023-03-01', age: 2, value: 54.8 },
      { date: '2023-05-01', age: 4, value: 59.5 },
      { date: '2023-07-01', age: 6, value: 63.9 },
      { date: '2023-10-01', age: 9, value: 68.4 },
      { date: '2024-01-01', age: 12, value: 73.5 },
      { date: '2024-07-01', age: 18, value: 78.9 },
      { date: '2025-01-01', age: 24, value: 83.8 },
      { date: '2026-01-01', age: 36, value: 92.5 },
      { date: '2027-01-01', age: 48, value: 99.7 },
      { date: '2028-01-01', age: 60, value: 107.1 },
    ],
  },

  // Weight measurements - weekly (0-13 weeks)
  'who-boys-weight-weekly': {
    gestationalAgeAtBirth: 34, // Born at 34 weeks (6 weeks premature)
    measurements: [
      { date: '2023-01-01', age: 0, value: 2.9 },
      { date: '2023-01-08', age: 1, value: 3.2 },
      { date: '2023-01-15', age: 2, value: 3.5 },
      { date: '2023-01-22', age: 3, value: 3.8 },
      { date: '2023-01-29', age: 4, value: 4.1 },
      { date: '2023-02-12', age: 6, value: 4.6 },
      { date: '2023-02-26', age: 8, value: 5.1 },
      { date: '2023-03-19', age: 11, value: 5.7 },
      { date: '2023-04-02', age: 13, value: 6.1 },
    ],
  },
  'who-girls-weight-weekly': {
    gestationalAgeAtBirth: 36, // Born at 36 weeks (4 weeks premature)
    measurements: [
      { date: '2023-01-01', age: 0, value: 2.8 },
      { date: '2023-01-08', age: 1, value: 3.0 },
      { date: '2023-01-15', age: 2, value: 3.3 },
      { date: '2023-01-22', age: 3, value: 3.6 },
      { date: '2023-01-29', age: 4, value: 3.8 },
      { date: '2023-02-12', age: 6, value: 4.3 },
      { date: '2023-02-26', age: 8, value: 4.7 },
      { date: '2023-03-19', age: 11, value: 5.2 },
      { date: '2023-04-02', age: 13, value: 5.6 },
    ],
  },

  // Weight measurements - monthly
  'who-boys-weight-monthly': {
    gestationalAgeAtBirth: 34, // Born at 34 weeks (6 weeks premature)
    measurements: [
      { date: '2023-01-01', age: 0, value: 2.9 },
      { date: '2023-02-01', age: 1, value: 3.8 },
      { date: '2023-03-01', age: 2, value: 4.7 },
      { date: '2023-05-01', age: 4, value: 6.0 },
      { date: '2023-07-01', age: 6, value: 7.2 },
      { date: '2023-10-01', age: 9, value: 8.5 },
      { date: '2024-01-01', age: 12, value: 9.3 },
      { date: '2024-07-01', age: 18, value: 10.7 },
      { date: '2025-01-01', age: 24, value: 11.9 },
      { date: '2026-01-01', age: 36, value: 14.1 },
      { date: '2027-01-01', age: 48, value: 15.9 },
      { date: '2028-01-01', age: 60, value: 17.8 },
    ],
  },
  'who-girls-weight-monthly': {
    gestationalAgeAtBirth: 36, // Born at 36 weeks (4 weeks premature)
    measurements: [
      { date: '2023-01-01', age: 0, value: 2.8 },
      { date: '2023-02-01', age: 1, value: 3.7 },
      { date: '2023-03-01', age: 2, value: 4.5 },
      { date: '2023-05-01', age: 4, value: 5.6 },
      { date: '2023-07-01', age: 6, value: 6.7 },
      { date: '2023-10-01', age: 9, value: 7.9 },
      { date: '2024-01-01', age: 12, value: 8.7 },
      { date: '2024-07-01', age: 18, value: 10.0 },
      { date: '2025-01-01', age: 24, value: 11.4 },
      { date: '2026-01-01', age: 36, value: 13.6 },
      { date: '2027-01-01', age: 48, value: 15.4 },
      { date: '2028-01-01', age: 60, value: 17.2 },
    ],
  },

  // BMI measurements - monthly
  'who-boys-bmi-monthly-0-5': {
    gestationalAgeAtBirth: 34, // Born at 34 weeks (6 weeks premature)
    measurements: [
      { date: '2023-01-01', age: 0, value: 12.5 },
      { date: '2023-02-01', age: 1, value: 14.1 },
      { date: '2023-03-01', age: 2, value: 15.3 },
      { date: '2023-05-01', age: 4, value: 16.1 },
      { date: '2023-07-01', age: 6, value: 16.6 },
      { date: '2023-10-01', age: 9, value: 17.0 },
      { date: '2024-01-01', age: 12, value: 16.5 },
      { date: '2024-07-01', age: 18, value: 16.3 },
      { date: '2025-01-01', age: 24, value: 16.1 },
      { date: '2026-01-01', age: 36, value: 15.7 },
      { date: '2027-01-01', age: 48, value: 15.4 },
      { date: '2028-01-01', age: 60, value: 15.1 },
    ],
  },
  'who-girls-bmi-monthly-0-5': {
    gestationalAgeAtBirth: 36, // Born at 36 weeks (4 weeks premature)
    measurements: [
      { date: '2023-01-01', age: 0, value: 12.5 },
      { date: '2023-02-01', age: 1, value: 14.1 },
      { date: '2023-03-01', age: 2, value: 14.8 },
      { date: '2023-05-01', age: 4, value: 15.5 },
      { date: '2023-07-01', age: 6, value: 16.3 },
      { date: '2023-10-01', age: 9, value: 16.5 },
      { date: '2024-01-01', age: 12, value: 16.0 },
      { date: '2024-07-01', age: 18, value: 15.9 },
      { date: '2025-01-01', age: 24, value: 15.8 },
      { date: '2026-01-01', age: 36, value: 15.5 },
      { date: '2027-01-01', age: 48, value: 15.3 },
      { date: '2028-01-01', age: 60, value: 15.0 },
    ],
  },

  // Head circumference measurements - weekly (0-13 weeks)
  'who-boys-head-circumference-weekly-0-13': {
    gestationalAgeAtBirth: 34, // Born at 34 weeks (6 weeks premature)
    measurements: [
      { date: '2023-01-01', age: 0, value: 32.8 },
      { date: '2023-01-08', age: 1, value: 33.9 },
      { date: '2023-01-15', age: 2, value: 35.0 },
      { date: '2023-01-22', age: 3, value: 36.0 },
      { date: '2023-01-29', age: 4, value: 36.9 },
      { date: '2023-02-12', age: 6, value: 38.2 },
      { date: '2023-02-26', age: 8, value: 39.3 },
      { date: '2023-03-19', age: 11, value: 40.5 },
      { date: '2023-04-02', age: 13, value: 41.2 },
    ],
  },
  'who-girls-head-circumference-weekly-0-13': {
    gestationalAgeAtBirth: 36, // Born at 36 weeks (4 weeks premature)
    measurements: [
      { date: '2023-01-01', age: 0, value: 32.6 },
      { date: '2023-01-08', age: 1, value: 33.6 },
      { date: '2023-01-15', age: 2, value: 34.5 },
      { date: '2023-01-22', age: 3, value: 35.4 },
      { date: '2023-01-29', age: 4, value: 36.1 },
      { date: '2023-02-12', age: 6, value: 37.3 },
      { date: '2023-02-26', age: 8, value: 38.3 },
      { date: '2023-03-19', age: 11, value: 39.4 },
      { date: '2023-04-02', age: 13, value: 40.0 },
    ],
  },

  // Head circumference measurements - monthly
  'who-boys-head-circumference-monthly-0-5': {
    gestationalAgeAtBirth: 34, // Born at 34 weeks (6 weeks premature)
    measurements: [
      { date: '2023-01-01', age: 0, value: 32.8 },
      { date: '2023-02-01', age: 1, value: 36.3 },
      { date: '2023-03-01', age: 2, value: 38.5 },
      { date: '2023-05-01', age: 4, value: 41.1 },
      { date: '2023-07-01', age: 6, value: 42.9 },
      { date: '2023-10-01', age: 9, value: 44.7 },
      { date: '2024-01-01', age: 12, value: 45.8 },
      { date: '2024-07-01', age: 18, value: 47.2 },
      { date: '2025-01-01', age: 24, value: 48.1 },
      { date: '2026-01-01', age: 36, value: 49.4 },
      { date: '2027-01-01', age: 48, value: 50.1 },
      { date: '2028-01-01', age: 60, value: 50.6 },
    ],
  },
  'who-girls-head-circumference-monthly-0-5': {
    gestationalAgeAtBirth: 36, // Born at 36 weeks (4 weeks premature)
    measurements: [
      { date: '2023-01-01', age: 0, value: 32.6 },
      { date: '2023-02-01', age: 1, value: 35.9 },
      { date: '2023-03-01', age: 2, value: 37.8 },
      { date: '2023-05-01', age: 4, value: 40.0 },
      { date: '2023-07-01', age: 6, value: 41.8 },
      { date: '2023-10-01', age: 9, value: 43.6 },
      { date: '2024-01-01', age: 12, value: 44.7 },
      { date: '2024-07-01', age: 18, value: 46.0 },
      { date: '2025-01-01', age: 24, value: 47.0 },
      { date: '2026-01-01', age: 36, value: 48.2 },
      { date: '2027-01-01', age: 48, value: 48.9 },
      { date: '2028-01-01', age: 60, value: 49.4 },
    ],
  },

  // Weight-for-length measurements
  'who-girls-weight-for-length-0-2': {
    measurements: [
      { length: 47.5, value: 2.9 },
      { length: 48.8, value: 3.2 },
      { length: 50.0, value: 3.5 },
    ],
  },
  'who-boys-weight-for-length-0-2': {
    measurements: [
      { length: 48.0, value: 3.1 },
      { length: 49.2, value: 3.4 },
      { length: 50.5, value: 3.7 },
    ],
  },
};

// Helper function to access the old structure through the new one for backward compatibility
export const mockPatientMeasurements: Record<string, PatientMeasurement[]> =
  Object.entries(mockPatientData).reduce<Record<string, PatientMeasurement[]>>(
    (acc, [key, value]) => {
      acc[key] = value.measurements;
      return acc;
    },
    {}
  );

// Helper to calculate adjusted age in weeks or months
export function getAdjustedAge(
  chronologicalAge: number,
  gestationalAgeAtBirth: number,
  intervalType: IntervalType
): number {
  // Calculate how many weeks premature
  const weeksPremature = FULL_TERM_WEEKS - gestationalAgeAtBirth;

  if (weeksPremature <= 0) {
    return chronologicalAge; // Not premature, no adjustment needed
  }

  // Convert weeks premature to the appropriate interval
  const adjustment =
    intervalType === 'week' ? weeksPremature : weeksPremature / 4.348; // Approximate weeks to months conversion

  // Adjusted age is chronological age minus adjustment, but never less than 0
  return Math.max(0, chronologicalAge - adjustment);
}
